/**
 * Environment configuration
 *
 * All environment variables should be accessed through this module.
 * This provides type safety and a single source of truth.
 *
 * Vite environment variables:
 * - Must be prefixed with VITE_ to be exposed to client code
 * - Access via import.meta.env.VITE_VARIABLE_NAME
 * - Define in .env, .env.local, .env.development, .env.production files
 */

import { parseFeatureFlags, buildFeatures } from "@/feature-flags/feature-flags";
import { parseLoginVariant, type LoginVariant } from "@/config/login-variant";
import {
  parseOrgSpecificContents,
  buildPrivacyPolicyUrl,
} from "@/org-specific-contents/org-specific-contents";

const REDIRECTION_ALLOWED_DOMAINS = (import.meta.env.VITE_REDIRECTION_ALLOWED_DOMAINS ?? "")
  .split(",")
  .map((domain: string) => domain.trim())
  .filter(Boolean);

const FEATURES = buildFeatures(parseFeatureFlags(import.meta.env.VITE_FEATURE_MANAGEMENT));
const PRIVACY_POLICY_URL = buildPrivacyPolicyUrl(
  parseOrgSpecificContents(import.meta.env.VITE_ORG_SPECIFIC_CONTENTS)
);

interface EnvConfig {
  /** Base URL for the API endpoints */
  apiBaseUrl: string;
  /** Google reCAPTCHA v3 site key */
  recaptchaSiteKey: string;
  /** Allowed domains for redirect URL validation (supports subdomain matching with leading dot) */
  allowedRedirectDomains: string[];
  /** Default redirect URL when no returnUrl is stored (empty string = fall back to /home) */
  redirectDefaultUrl: string;
  /** Privacy policy URL from `VITE_ORG_SPECIFIC_CONTENTS` JSON (`PRIVACY_POLICY_URL` key) */
  privacyPolicyUrl: string;
  /** Feature flags */
  features: {
    emailLogin: boolean;
    kretaLogin: boolean;
    googleLogin: boolean;
    elteLogin: boolean;
    register: boolean;
  };
  /** Login page variant: default (AuthCard) or elte (full landing layout) */
  loginVariant: LoginVariant;
}

export const env: EnvConfig = {
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? "/api",
  recaptchaSiteKey: import.meta.env.VITE_RECAPTCHA_SITE_KEY ?? "",
  allowedRedirectDomains: REDIRECTION_ALLOWED_DOMAINS,
  redirectDefaultUrl: import.meta.env.VITE_REDIRECTION_DEFAULT_RETURNURL ?? "",
  privacyPolicyUrl: PRIVACY_POLICY_URL,
  features: FEATURES,
  loginVariant: parseLoginVariant(import.meta.env.VITE_LOGIN_VARIANT),
};
